import * as React from "react";
import { Form, Option } from "informedv1";
import { TextBox } from "../../forms/TextBox";
import { Required } from "../../forms/validation/Required";
import { isModelWithError, ReportingServicesCatalogItem, Query } from "../../misc/api/apiTypes";
import { Dropdown } from "../../forms/Dropdown";
import { ImportSummary } from "./ImportSummary";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

export interface BaseReportImportForm {
    form: any;
    setImportResults: React.Dispatch<React.SetStateAction<any[]>>;
    importResults: any[];
}

export const BaseReportImportForm = (props: BaseReportImportForm) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [baseReports, setBaseReports] = React.useState<ReportingServicesCatalogItem[]>([]);
    const [isLoadingBaseReports, setIsLoadingBaseReports] = React.useState(false);
    const [foldersQuery, setFoldersQuery] = React.useState<Query<ReportingServicesCatalogItem>>(
        null
    );

    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(
        () => {
            const fetchData = async () => {
                setIsLoadingBaseReports(true);
                const baseReports = await legacyClient.getBaseReportsMetadata(
                    props.form.ServerAddress,
                    props.form.Username,
                    props.form.Password
                );
                const folders = await legacyClient.getFolders(
                    props.form.ServerAddress,
                    props.form.Username,
                    props.form.Password
                ).then(json => {
                    if (isModelWithError(json)) {
                        throw new Error(json.Errors[0].Message);
                    }
                    return json;
                });
                setFoldersQuery(folders);

                const dataSource: ReportingServicesCatalogItem = { Id: '00000000-0000-0000-0000-000000000000',  Name: 'BaseReportShared', Type: 'DataSource' };
                const catalogItems = [ ...baseReports, dataSource ];
                setBaseReports(catalogItems);
                setIsLoadingBaseReports(false);
            };

            fetchData();
        },
        [props.form]
    );

    const onSubmit = async form => {
        setIsSubmitting(true);
        const importResults = await legacyClient.importBaseReports(
            props.form.ServerAddress,
            props.form.Username,
            props.form.Password,
            form.Folder,
            form.ConnectionString
        );

        if (isModelWithError(importResults)) {
            const errors = importResults.Errors;
        } else {
            props.setImportResults(importResults);
        }

        setIsSubmitting(false);
    };

    return (
        <Form onSubmit={onSubmit}>
            <>
                <ImportSummary
                    isLoadingBaseReports={isLoadingBaseReports}
                    baseReports={baseReports}
                    importResults={props.importResults}
                />
                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group row">
                            <div className="col-sm-4">
                                <label htmlFor="Folder" className="pull-right">
                                    Folder
                                </label>
                            </div>
                            <div className="col-sm-8">
                                <p>Enter the name of the folder to import the items into</p>
                                <Dropdown id="Folder" field="Folder" validate={Required}>
                                    <Option key="OptionBlank" value={null} />
                                    {foldersQuery &&
                                        foldersQuery.Data.map(x => (
                                            <Option key={`Option${x.Id}`} value={x.Name}>
                                                {x.Name}
                                            </Option>
                                        ))}
                                </Dropdown>
                            </div>
                        </div>

                        <div className="form-group row">
                            <div className="col-sm-4">
                                <label htmlFor="ConnectionString" className="pull-right">
                                    Connection String
                                </label>
                            </div>
                            <div className="col-sm-8">
                                <p>
                                    Enter the default connection string to be set for the shared
                                    report data source
                                </p>
                                <TextBox
                                    id="ConnectionString"
                                    field="ConnectionString"
                                    placeholder="Server=myServerAddress;Database=myDataBase;User Id=myUsername;Password=myPassword;"
                                    validate={Required}
                                />
                            </div>
                        </div>

                        <div className="form-group row">
                            <div className="col-sm-12">
                                <AsyncActionButton isSubmitting={isSubmitting || isLoadingBaseReports}
                                    className="btn btn-primary pull-right"
                                    type="submit"
                                >
                                    <i className="fa fa-cloud-upload" /> Import
                                </AsyncActionButton>
                                &nbsp;
                            </div>
                        </div>
                    </div>
                </div>
            </>
        </Form>
    );
};
